import { useQuery } from '@tanstack/react-query';
import { LZ_SCAN_API_URL } from '../config/oft';

export type BridgeStatus = 'pending' | 'inflight' | 'delivered' | 'failed';

export interface OftBridgeStatusData {
  status: BridgeStatus;
  srcTxHash: string;
}

interface LzScanResponse {
  messages?: Array<{
    status: string;
  }>;
}

/**
 * Maps LayerZero Scan API status names to our BridgeStatus type.
 */
function mapLzStatus(lzStatusName: string): BridgeStatus {
  const normalized = lzStatusName.toLowerCase();
  if (normalized === 'delivered' || normalized === 'success') {
    return 'delivered';
  }
  if (normalized === 'failed' || normalized === 'blocked') {
    return 'failed';
  }
  if (normalized === 'inflight' || normalized === 'confirming') {
    return 'inflight';
  }
  return 'pending';
}

/**
 * Polls the LayerZero Scan API to track bridge message status.
 *
 * Refetches every 10 seconds while status is not 'delivered' or 'failed'.
 * Uses TanStack Query's refetchInterval to handle the polling.
 *
 * @param srcTxHash - Source chain transaction hash of the OFT send
 */
export function useOftBridgeStatus(srcTxHash: string | undefined) {
  return useQuery<OftBridgeStatusData>({
    queryKey: ['oftBridgeStatus', srcTxHash],
    queryFn: async () => {
      if (!srcTxHash) {
        throw new Error('srcTxHash is required');
      }

      const response = await fetch(`${LZ_SCAN_API_URL}/${srcTxHash}`);

      if (!response.ok) {
        // API may return 404 while the tx is still being indexed
        if (response.status === 404) {
          return { status: 'pending', srcTxHash };
        }
        throw new Error(`LZ Scan API error: ${response.status}`);
      }

      const data: LzScanResponse = await response.json();

      if (!data.messages || data.messages.length === 0) {
        return { status: 'pending', srcTxHash };
      }

      const firstMessage = data.messages[0];
      const status = mapLzStatus(firstMessage.status);

      return { status, srcTxHash };
    },
    enabled: !!srcTxHash,
    // Poll every 10 seconds while not in terminal state
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'delivered' || status === 'failed') {
        return false; // stop polling
      }
      return 10_000; // poll every 10s
    },
    staleTime: 0, // always treat as stale for polling
  });
}
